import { PropsWithChildren, useEffect } from "react";
import { ActionListContext } from "./action.list";
import { ActionItems } from "./action.list.items";
import {
  getUserControllerMeQueryKey,
  useUserControllerMe,
  useUserKYCControllerGetChallenges,
  useUserPartControllerMyParticipants,
} from "@/generated/api/api";
import { useAccessTokenClaims, useSession } from "@/utils/session.ctx";
import { useQueryClient } from "@tanstack/react-query";
import { getActiveKYC } from "@/utils/kyc.util";
import { Platform } from "react-native";

export function ActionContextProvider(props: PropsWithChildren) {
  const { data: user } = useUserControllerMe();
  const { data: myParticipants } = useUserPartControllerMyParticipants();
  const queryClient = useQueryClient();

  const kyc = getActiveKYC(user?.kyc);

  const { data: kycChallenges } = useUserKYCControllerGetChallenges({
    query: {
      enabled: !!kyc,
      refetchInterval: (query) =>
        !!kyc && !!getActiveKYC(query.state.data) ? 1 * 60 * 1000 : false, // each 1 minute refetch
    },
  });

  useEffect(() => {
    if (kyc && !getActiveKYC(kycChallenges)) {
      queryClient.invalidateQueries({
        queryKey: getUserControllerMeQueryKey(),
      });
    }
  }, [kyc, kycChallenges]);

  const actions = [];

  if (user?.tipsRestricted) {
    actions.push(<ActionItems.TIPS_RESTRICTED />);
  }

  if (user?.payoutsRestricted) {
    actions.push(<ActionItems.PAYOUT_RESTRICTED />);
  }

  if (Platform.OS === "web") {
    actions.push(<ActionItems.DOWNLOAD_APP />);
  }

  if (user && (!user.firstName || !user.lastName)) {
    actions.push(<ActionItems.PROFILE />);
  }

  if (myParticipants && myParticipants.length === 0) {
    actions.push(<ActionItems.CREATE_JOIN_RESTAURANT />);
  }

  if (
    user?.beneficiary?.status === "PENDING" ||
    user?.beneficiary?.status === "DECLINED"
  ) {
    actions.push(
      <ActionItems.BENEFICIARY_UPDATE status={user.beneficiary.status} />
    );
  }

  if (kyc) {
    actions.push(<ActionItems.KYC challenge={kyc} />);
  }

  return (
    <ActionListContext.Provider value={{ actions }}>
      {props.children}
    </ActionListContext.Provider>
  );
}
